<script setup>
import { ref, computed, onMounted } from 'vue'
import Main from './Main.vue'
import { Icon, Editable, Button } from '@components'
import { sortedDays } from '@utils'

import { useCustomizer } from '@store/customizer'
const $customizer = useCustomizer()

import { useSettings } from '@store/settings'
const $settings = useSettings()

import moment from 'moment'

const pageDetails = ref({
    layoutTitle: 'Time',
    // frontendTitle: 'Select date and time'
})

const useCalendar = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const activeCalender = ref({
        month: 1, // Specify the month (1 for January, 2 for February, etc.)
        year: 0, // Specify the year
        dates: [],
    })

    onMounted(() => {
        activeCalender.value.month = new Date().getMonth() + 1
        activeCalender.value.year = new Date().getFullYear()
    })

    // generate dates
    const generateDates = () => {
        const selectedDate = moment()
        const startDayOfCalenderWeek = sortedDays()[0]

        activeCalender.value.dates = []

        let dayToAddStart = (selectedDate.day() + 7 - days.indexOf(startDayOfCalenderWeek)) % 7
        let dateStart = selectedDate.subtract(dayToAddStart, 'days')

        for (let i = 0; i < 7; i++) {
            activeCalender.value.dates.push({
                day: dateStart.format('dddd'),
                date: dateStart.date(),
                month: dateStart.format('MMMM'),
                year: dateStart.format('YYYY'),
                fullDate: dateStart.format('YYYY-MM-DD'),
            })

            dateStart.add(1, 'days')
        }

    }

    // get dates for calender
    const getDates = computed(() => {
        generateDates()
        return activeCalender.value.dates
    })

    // check if today
    const isToday = (fullDate) => {
        return moment().format('YYYY-MM-DD') === fullDate
    }

    // check if current month
    const isCurrentMonth = (fullDate) => {
        return moment(`${activeCalender.value.year}-${activeCalender.value.month}`, 'YYYY-M').format('YYYY-MM') === moment(fullDate, 'YYYY-MM-DD').format('YYYY-MM')
    }


    return { getDates, isToday, isCurrentMonth, activeCalender }
}
const { getDates, isToday, isCurrentMonth, activeCalender } = useCalendar()

const formatTime = (time, format) => {
    if ($customizer.getMenuStatusById('time', 'show_time')) {
        return moment(time, format).format('HH:mm')
    } else {
        return moment(time, format).format('hh:mm A')
    }
}

const timePeriod = (slot_start) => {
    const hour = moment(slot_start, 'HH:mm').hour()

    if (hour >= 6 && hour < 12) {
        return 'Morning'
    } else if (hour >= 12 && hour < 18) {
        return 'Afternoon'
    } else {
        return 'Evening'
    }
}

const timeIcon = ref({
    Morning: 'sunSetting02',
    Afternoon: 'sun',
    Evening: 'moonStar',
})

const timeSlots = computed(() => {
    return [
        { start: '09:00' },
        { start: '10:00' },
        { start: '11:00' },
        { start: '12:00' },
        { start: '13:00' },
        { start: '19:00' },
    ]
})
</script>

<template>
    <Main :pageTitle="pageDetails.layoutTitle" @submit="$customizer.publish" :previous="{ name: 'customizer.form.date' }" :next="{ name: 'customizer.form.account' }">
        <Editable v-model="customizer.time.title" :iconVisibility="false" border inputClass="text-xl" :inputStyle="$customizer.titleStyle"></Editable>

        <div class="mini-calendar-wrapper">
            <!-- calendar header  -->
            <div class="flex justify-between items-center">
                <div class=" text-base leading-6 font-semibold text-[#344054]">{{ moment(`${activeCalender.year}-${activeCalender.month}-01`, 'YYYY-MM').format('MMMM YYYY') || 'N/A' }}</div>
                <Icon name="calendar" size="20px" class="text-[#667085] m-2 cursor-pointer hover:text-[#222]" @click="$router.push({ name: 'customizer.form.date' })" />
            </div>

            <!-- divider  -->
            <div class="w-full h-px bg-[#eaecf0] my-2"></div>

            <div class="flex items-end gap-2">
                <div class="w-full">

                    <!-- calendar days  -->
                    <div class="grid grid-cols-7 gap-7 justify-items-center items-center mb-2">
                        <div v-for="day in sortedDays()" :key="day" class="flex justify-center items-center w-10 h-10 text-sm leading-5 font-medium text-[#667085]">{{ day.slice(0, 3) }}</div>
                    </div>

                    <!-- calendar dates  -->
                    <div class="grid grid-cols-7 gap-7 justify-items-center items-center">
                        <div v-for="item in getDates" :key="item" class="relative overflow-hidden flex justify-center items-center w-14 h-10 text-base leading-6 font-semibold" :class="{ 'text-white border-2 border-solid': isToday(item.fullDate), 'border cursor-pointer text-[#344054] border-[#e9d7fe]': isCurrentMonth(item.fullDate) && !isToday(item.fullDate) }, { 'rounded': $customizer.isRounded }" :style="`background-color: ${isToday(item.fullDate) ? `${customizer.design.brand_color} !important` : 'transparent'}; border-color: ${isToday(item.fullDate) ? `${customizer.design.brand_color} !important` : ''}`">
                            <div class="z-10">{{ item.date }}</div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <!-- divider -->
        <div class="w-full h-[1px] bg-[#EAECF0] my-5"></div>

        <div class="text-sm leading-5 font-normal text-[#475467] mb-3">
            <Editable v-model="customizer.time.slot_subtitle" :iconVisibility="false" border></Editable> {{ moment().format('MMMM DD') }}
        </div>

        <div class="grid grid-cols-3 gap-3 items-center">
            <div v-for="time in timeSlots" class="flex gap-1.5 justify-center items-center transition-all ease-in-out duration-300 cursor-pointer bg-[#f2f4f7] px-3 py-2" :class="{ 'rounded': $customizer.isRounded }">
                <Icon v-if="$customizer.getMenuStatusById('time', 'show_time_icon')" :name="timeIcon[timePeriod(time.start) || 'Morning']" size="16px" />
                <div class="text-base leading-6 font-medium">{{ formatTime(time.start, 'HH:mm') }}</div>
            </div>
        </div>

        <template #footer>
            <Button type="primary" size="md" radius="square" class="w-full" :buttonStyle="$customizer.buttonStyle">
                <Editable v-model="customizer.time.continue_button" :iconVisibility="false" border></Editable>
            </Button>
        </template>
    </Main>
</template>
